import { useState, useMemo } from 'react';
import { useParticipante } from '../../../contexts/ParticipanteContext';
import { ShareModal } from '../../../components/social/ShareModal';

export default function DistintivosPage() {
  const { campanhaAtual, todosDistintivos } = useParticipante();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<any>(null);
  
  const [filtroCategoria, setFiltroCategoria] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'conquistados' | 'bloqueados'>('todos');
  const [distintivoSelecionado, setDistintivoSelecionado] = useState<any>(null);
  const [showConquistaAnimation, setShowConquistaAnimation] = useState(false);
  const [distintivoConquistado, setDistintivoConquistado] = useState<any>(null);
  const [showProximosDistintivos, setShowProximosDistintivos] = useState(false);
  const [showCompartilhar, setShowCompartilhar] = useState(false);

  // Usar todos os distintivos, marcando quais foram conquistados na campanha atual
  const distintivos = useMemo(() => {
    if (!campanhaAtual?.progressao) return todosDistintivos;
    const conquistados = campanhaAtual.progressao.distintivosConquistados || [];
    const conquistadosIds = new Set(conquistados.map((d: any) => d.id));
    // Merge: todos os distintivos base, sobrescrevendo com dados de conquista da campanha
    return todosDistintivos.map(d => {
      if (conquistadosIds.has(d.id)) {
        const conquistado = conquistados.find((c: any) => c.id === d.id);
        return { ...d, conquistado: true, dataConquista: conquistado?.dataConquista };
      }
      return { ...d, conquistado: false, dataConquista: undefined };
    });
  }, [campanhaAtual, todosDistintivos]);

  const categorias = [
    { id: 'todos', label: 'Todos', icon: 'ri-grid-line' },
    { id: 'Iniciante', label: 'Iniciante', icon: 'ri-seedling-line' },
    { id: 'Vendas', label: 'Vendas', icon: 'ri-shopping-cart-line' },
    { id: 'Serviços', label: 'Serviços', icon: 'ri-customer-service-line' },
    { id: 'Clientes', label: 'Clientes', icon: 'ri-user-heart-line' },
    { id: 'Elite', label: 'Elite', icon: 'ri-vip-crown-line' },
    { id: 'Jogos', label: 'Jogos', icon: 'ri-gamepad-line' },
    { id: 'Social', label: 'Social', icon: 'ri-fire-line' }
  ];

  // Função para obter o ícone baseado na categoria
  const getCategoriaIcon = (categoria: string) => {
    switch (categoria) {
      case 'Iniciante': return 'ri-seedling-line';
      case 'Vendas': return 'ri-shopping-cart-line';
      case 'Serviços': return 'ri-customer-service-line';
      case 'Clientes': return 'ri-user-heart-line';
      case 'Elite': return 'ri-vip-crown-line';
      case 'Jogos': return 'ri-gamepad-line';
      case 'Social': return 'ri-fire-line';
      default: return 'ri-medal-line';
    }
  };

  const getRaridadeColor = (raridade: string) => {
    switch (raridade) {
      case 'comum': return 'text-slate-600 bg-slate-100';
      case 'raro': return 'text-blue-600 bg-blue-100';
      case 'épico': return 'text-purple-600 bg-purple-100';
      case 'lendário': return 'text-orange-600 bg-orange-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  const getRaridadeLabel = (raridade: string) => {
    switch (raridade) {
      case 'comum': return 'Comum';
      case 'raro': return 'Raro';
      case 'épico': return 'Épico';
      case 'lendário': return 'Lendário';
      default: return raridade;
    }
  };

  const distintivosConquistados = distintivos.filter(d => d.conquistado).length;
  const totalDistintivos = campanhaAtual?.progressao?.totalDistintivos || distintivos.length;
  const distintivosRaros = distintivos.filter(d => d.conquistado && d.raridade !== 'comum').length;

  const distintivosFiltrados = distintivos.filter(d => {
    const categoriaMatch = filtroCategoria === 'todos' || d.categoria === filtroCategoria;
    const statusMatch = 
      filtroStatus === 'todos' ||
      (filtroStatus === 'conquistados' && d.conquistado) ||
      (filtroStatus === 'bloqueados' && !d.conquistado);
    return categoriaMatch && statusMatch;
  });

  // Simular conquista de distintivo (para demonstração)
  const simularConquista = (distintivo: any) => {
    setDistintivoConquistado(distintivo);
    setShowConquistaAnimation(true);
    
    // Ocultar após 5 segundos
    setTimeout(() => {
      setShowConquistaAnimation(false);
    }, 5000);
  };

  // Obter próximos distintivos sugeridos
  const getProximosDistintivos = () => {
    return distintivos
      .filter(d => !d.conquistado && d.progresso !== undefined)
      .sort((a, b) => {
        const progressoA = (a.progresso / a.meta) * 100;
        const progressoB = (b.progresso / b.meta) * 100;
        return progressoB - progressoA;
      })
      .slice(0, 3);
  };

  const proximosDistintivos = getProximosDistintivos();

  // Função para compartilhar distintivo
  const compartilharDistintivo = (distintivo: any) => {
    setDistintivoSelecionado(distintivo);
    setShowCompartilhar(true);
  };

  const handleShare = (badge: any) => {
    setSelectedBadge({
      title: badge.nome,
      description: badge.descricao,
      type: 'badge' as const
    });
    setShareModalOpen(true);
  };

  const compartilharRede = (rede: string) => {
    const texto = `🏆 Acabei de conquistar o distintivo "${distintivoSelecionado?.nome}" na campanha ${campanhaAtual?.nome}! #Conquista #Gamificação`;
    const url = window.location.href;

    let shareUrl = '';
    
    switch (rede) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(texto)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(texto)}&url=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(texto + ' ' + url)}`;
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  const copiarLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copiado para a área de transferência!');
  };

  // Se não houver campanha selecionada ou campanha sem distintivos
  if (!campanhaAtual || !campanhaAtual.recursos?.distintivos) {
    return (
      <div className="space-y-6 pb-20 lg:pb-6">
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="bg-white rounded-2xl shadow-xl p-12">
            <i className="ri-medal-line text-6xl text-slate-300 mb-6"></i>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {!campanhaAtual ? 'Nenhuma Campanha Selecionada' : 'Campanha sem Distintivos'}
            </h2>
            <p className="text-slate-600 mb-8">
              {!campanhaAtual 
                ? 'Selecione uma campanha no header para visualizar os distintivos disponíveis.'
                : 'A campanha selecionada não possui o sistema de distintivos ativo.'
              }
            </p>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/premiado/campanhas')}
              className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium whitespace-nowrap"
            >
              Ver Campanhas Disponíveis
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      {/* Animação de Conquista */}
      {showConquistaAnimation && distintivoConquistado && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 animate-fade-in">
          {/* Confetes */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 100 }).map((_, i) => {
              const colors = ['#FF6B6B', '#4ECDC4', '#95E1D3', '#F38181', '#AA96DA', '#FCBAD3', '#FFFFD2', '#A8E6CF'];
              const randomColor = colors[Math.floor(Math.random() * colors.length)];
              const randomLeft = Math.random() * 100;
              const randomDelay = Math.random() * 0.5;
              const randomDuration = 2 + Math.random() * 2;
              const randomRotation = Math.random() * 360;
              const randomSize = 8 + Math.random() * 8;
              
              return (
                <div
                  key={i}
                  className="absolute animate-confetti"
                  style={{
                    left: `${randomLeft}%`,
                    top: '-20px',
                    width: `${randomSize}px`,
                    height: `${randomSize}px`,
                    backgroundColor: randomColor,
                    animationDelay: `${randomDelay}s`,
                    animationDuration: `${randomDuration}s`,
                    transform: `rotate(${randomRotation}deg)`,
                    borderRadius: Math.random() > 0.5 ? '50%' : '0'
                  }}
                />
              );
            })}
          </div>

          {/* Card de Conquista */}
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 animate-scale-in shadow-2xl">
            <div className="text-center">
              <div className="text-6xl mb-4 animate-bounce">🎉</div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">Parabéns!</h2>
              <p className="text-slate-600 mb-6">Você conquistou um novo distintivo!</p>
              
              <div className={`w-32 h-32 mx-auto rounded-full flex items-center justify-center shadow-2xl mb-4 ${
                distintivoConquistado.categoria === 'Iniciante' ? 'bg-green-500' :
                distintivoConquistado.categoria === 'Vendas' ? 'bg-blue-500' :
                distintivoConquistado.categoria === 'Serviços' ? 'bg-purple-500' :
                distintivoConquistado.categoria === 'Clientes' ? 'bg-pink-500' :
                distintivoConquistado.categoria === 'Elite' ? 'bg-yellow-500' :
                distintivoConquistado.categoria === 'Jogos' ? 'bg-indigo-500' :
                distintivoConquistado.categoria === 'Social' ? 'bg-red-500' :
                'bg-orange-500'
              } animate-pulse`}>
                <i className={`${getCategoriaIcon(distintivoConquistado.categoria)} text-5xl text-white`}></i>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mb-2">{distintivoConquistado.nome}</h3>
              <p className="text-slate-600 mb-6">{distintivoConquistado.descricao}</p>

              <div className="flex gap-3">
                <button
                  onClick={() => compartilharDistintivo(distintivoConquistado)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap"
                >
                  <i className="ri-share-line mr-2"></i>
                  Compartilhar
                </button>
                <button
                  onClick={() => setShowConquistaAnimation(false)}
                  className="flex-1 px-6 py-3 bg-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-300 transition-colors whitespace-nowrap"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Compartilhamento */}
      {showCompartilhar && distintivoSelecionado && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Compartilhar Conquista</h3>
              <p className="text-slate-600">Mostre sua conquista para seus amigos!</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => compartilharRede('facebook')}
                className="p-4 bg-[#1877F2] text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                aria-label="Compartilhar no Facebook"
              >
                <i className="ri-facebook-fill text-2xl"></i>
                Facebook
              </button>
              <button
                onClick={() => compartilharRede('twitter')}
                className="p-4 bg-[#1DA1F2] text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                aria-label="Compartilhar no Twitter"
              >
                <i className="ri-twitter-fill text-2xl"></i>
                Twitter
              </button>
              <button
                onClick={() => compartilharRede('linkedin')}
                className="p-4 bg-[#0A66C2] text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                aria-label="Compartilhar no LinkedIn"
              >
                <i className="ri-linkedin-fill text-2xl"></i>
                LinkedIn
              </button>
              <button
                onClick={() => compartilharRede('whatsapp')}
                className="p-4 bg-[#25D366] text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                aria-label="Compartilhar no WhatsApp"
              >
                <i className="ri-whatsapp-fill text-2xl"></i>
                WhatsApp
              </button>
            </div>

            <button
              onClick={copiarLink}
              className="w-full p-4 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors flex items-center justify-center gap-2 mb-4"
              aria-label="Copiar link"
            >
              <i className="ri-link text-xl"></i>
              Copiar Link
            </button>

            <button
              onClick={() => setShowCompartilhar(false)}
              className="w-full p-3 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors whitespace-nowrap"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Cabeçalho */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Distintivos de Progresso</h1>
        <p className="text-gray-600">
          Conquiste distintivos completando desafios na campanha <strong>{campanhaAtual.nome}</strong>
        </p>
      </div>

      {/* Card da Campanha Selecionada */}
      <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-6 mb-8 shadow-xl">
        <div className="flex items-center gap-4 text-white">
          <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-medal-line text-3xl"></i>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold">
              {campanhaAtual.nome}
            </h3>
            <p className="text-sm text-white/90">
              {campanhaAtual.descricao}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">
              {distintivosConquistados}/{totalDistintivos}
            </div>
            <div className="text-sm text-white/90">distintivos</div>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <i className="ri-medal-line text-4xl text-orange-600"></i>
            <div className="text-right">
              <div className="text-3xl font-bold text-slate-900">{distintivosConquistados}</div>
              <div className="text-sm text-slate-600">de {totalDistintivos}</div>
            </div>
          </div>
          <div className="text-sm font-medium text-slate-700">Distintivos Conquistados</div>
          <div className="mt-3 bg-slate-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-orange-500 to-red-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${totalDistintivos > 0 ? (distintivosConquistados / totalDistintivos) * 100 : 0}%` }}
            ></div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <i className="ri-star-line text-4xl text-purple-600"></i>
            <div className="text-right">
              <div className="text-3xl font-bold text-slate-900">{distintivosRaros}</div>
              <div className="text-sm text-slate-600">raros+</div>
            </div>
          </div>
          <div className="text-sm font-medium text-slate-700">Distintivos Raros</div>
          <div className="mt-2 text-xs text-slate-500">
            Épicos e Lendários conquistados
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <i className="ri-trophy-line text-4xl text-yellow-600"></i>
            <div className="text-right">
              <div className="text-3xl font-bold text-slate-900">
                {totalDistintivos > 0 ? Math.round((distintivosConquistados / totalDistintivos) * 100) : 0}%
              </div>
              <div className="text-sm text-slate-600">completo</div>
            </div>
          </div>
          <div className="text-sm font-medium text-slate-700">Progresso Total</div>
          <div className="mt-2 text-xs text-slate-500">
            Continue conquistando!
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6 mb-8">
        {/* Filtro de Categoria */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-slate-700 mb-3">Categoria</label>
          <div className="flex flex-wrap gap-2">
            {categorias.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setFiltroCategoria(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                  filtroCategoria === cat.id
                    ? 'bg-orange-600 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <i className={cat.icon}></i>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Filtro de Status */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-3">Status</label>
          <div className="flex gap-2">
            {[
              { id: 'todos', label: 'Todos' },
              { id: 'conquistados', label: 'Conquistados' },
              { id: 'bloqueados', label: 'Bloqueados' }
            ].map((status) => (
              <button
                key={status.id}
                onClick={() => setFiltroStatus(status.id as any)}
                className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                  filtroStatus === status.id
                    ? 'bg-orange-600 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid de Distintivos */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {distintivosFiltrados.map((distintivo) => (
          <div
            key={distintivo.id}
            onClick={() => setDistintivoSelecionado(distintivo)}
            className={`bg-white rounded-xl shadow-md border-2 p-6 cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
              distintivo.conquistado
                ? 'border-green-500'
                : 'border-slate-200 opacity-60 grayscale'
            }`}
          >
            {/* Ícone */}
            <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center shadow-lg ${
              distintivo.conquistado
                ? distintivo.categoria === 'Iniciante' ? 'bg-green-500' :
                  distintivo.categoria === 'Vendas' ? 'bg-blue-500' :
                  distintivo.categoria === 'Serviços' ? 'bg-purple-500' :
                  distintivo.categoria === 'Clientes' ? 'bg-pink-500' :
                  distintivo.categoria === 'Elite' ? 'bg-yellow-500' :
                  distintivo.categoria === 'Jogos' ? 'bg-indigo-500' :
                  distintivo.categoria === 'Social' ? 'bg-red-500' :
                  'bg-orange-500'
                : 'bg-slate-400'
            }`}>
              <i className={`${getCategoriaIcon(distintivo.categoria)} text-3xl text-white`}></i>
            </div>

            {/* Nome */}
            <h3 className="font-bold text-center text-slate-900 mb-2 line-clamp-2 min-h-[3rem]">
              {distintivo.nome}
            </h3>

            {/* Raridade */}
            <div className="flex justify-center mb-3">
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${getRaridadeColor(distintivo.raridade)}`}>
                {getRaridadeLabel(distintivo.raridade)}
              </span>
            </div>

            {/* Status */}
            {distintivo.conquistado ? (
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-green-600 font-semibold mb-1">
                  <i className="ri-check-line"></i>
                  <span className="text-sm">Conquistado</span>
                </div>
                <div className="text-xs text-slate-600">{distintivo.dataConquista}</div>
                
                {/* Botão Compartilhar */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShare(distintivo);
                  }}
                  className="mt-3 w-full px-3 py-1.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg text-xs font-medium hover:from-teal-600 hover:to-cyan-600 transition-all flex items-center justify-center gap-1"
                >
                  <i className="ri-share-line"></i>
                  Compartilhar
                </button>
              </div>
            ) : (
              <div>
                {distintivo.progresso !== undefined && distintivo.meta !== undefined && (
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-slate-600 mb-1">
                      <span>Progresso</span>
                      <span>{distintivo.progresso}/{distintivo.meta}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          distintivo.categoria === 'Iniciante' ? 'bg-green-500' :
                          distintivo.categoria === 'Vendas' ? 'bg-blue-500' :
                          distintivo.categoria === 'Serviços' ? 'bg-purple-500' :
                          distintivo.categoria === 'Clientes' ? 'bg-pink-500' :
                          distintivo.categoria === 'Elite' ? 'bg-yellow-500' :
                          distintivo.categoria === 'Jogos' ? 'bg-indigo-500' :
                          distintivo.categoria === 'Social' ? 'bg-red-500' :
                          'bg-orange-500'
                        }`}
                        style={{ width: `${(distintivo.progresso / distintivo.meta) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                <div className="text-center text-xs text-slate-500">
                  <i className="ri-lock-line mr-1"></i>
                  Bloqueado
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Mensagem quando não há distintivos filtrados */}
      {distintivosFiltrados.length === 0 && (
        <div className="text-center py-12">
          <i className="ri-medal-line text-6xl text-slate-300 mb-4"></i>
          <p className="text-slate-600 text-lg">
            Nenhum distintivo encontrado com os filtros selecionados
          </p>
        </div>
      )}

      {/* Modal de Detalhes */}
      {distintivoSelecionado && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDistintivoSelecionado(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            {/* Ícone */}
            <div className={`w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center shadow-2xl ${
              distintivoSelecionado.conquistado
                ? distintivoSelecionado.categoria === 'Iniciante' ? 'bg-green-500' :
                  distintivoSelecionado.categoria === 'Vendas' ? 'bg-blue-500' :
                  distintivoSelecionado.categoria === 'Serviços' ? 'bg-purple-500' :
                  distintivoSelecionado.categoria === 'Clientes' ? 'bg-pink-500' :
                  distintivoSelecionado.categoria === 'Elite' ? 'bg-yellow-500' :
                  distintivoSelecionado.categoria === 'Jogos' ? 'bg-indigo-500' :
                  distintivoSelecionado.categoria === 'Social' ? 'bg-red-500' :
                  'bg-orange-500'
                : 'bg-slate-400'
            } ${
              !distintivoSelecionado.conquistado && 'grayscale opacity-60'
            }`}>
              <i className={`${getCategoriaIcon(distintivoSelecionado.categoria)} text-5xl text-white`}></i>
            </div>

            {/* Nome e Raridade */}
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">{distintivoSelecionado.nome}</h3>
              <span className={`inline-block text-sm font-semibold px-4 py-1 rounded-full ${getRaridadeColor(distintivoSelecionado.raridade)}`}>
                {getRaridadeLabel(distintivoSelecionado.raridade)}
              </span>
            </div>

            {/* Descrição */}
            <p className="text-slate-700 text-center mb-6">{distintivoSelecionado.descricao}</p>

            {/* Requisitos */}
            <div className="bg-slate-50 rounded-lg p-4 mb-6 space-y-3">
              {distintivoSelecionado.xpNecessario !== undefined && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">XP Necessário:</span>
                  <span className="font-bold text-slate-900">{distintivoSelecionado.xpNecessario.toLocaleString()} XP</span>
                </div>
              )}
              {distintivoSelecionado.nivelMinimo !== undefined && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Nível Mínimo:</span>
                  <span className="font-bold text-slate-900">{distintivoSelecionado.nivelMinimo}</span>
                </div>
              )}
              {distintivoSelecionado.missaoObrigatoria && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Missão:</span>
                  <span className="font-bold text-slate-900">{distintivoSelecionado.missaoObrigatoria}</span>
                </div>
              )}
            </div>

            {/* Progresso */}
            {!distintivoSelecionado.conquistado && distintivoSelecionado.progresso !== undefined && distintivoSelecionado.meta !== undefined && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-slate-600 mb-2">
                  <span>Seu Progresso</span>
                  <span className="font-bold">{distintivoSelecionado.progresso}/{distintivoSelecionado.meta}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full ${
                      distintivoSelecionado.categoria === 'Iniciante' ? 'bg-green-500' :
                      distintivoSelecionado.categoria === 'Vendas' ? 'bg-blue-500' :
                      distintivoSelecionado.categoria === 'Serviços' ? 'bg-purple-500' :
                      distintivoSelecionado.categoria === 'Clientes' ? 'bg-pink-500' :
                      distintivoSelecionado.categoria === 'Elite' ? 'bg-yellow-500' :
                      distintivoSelecionado.categoria === 'Jogos' ? 'bg-indigo-500' :
                      distintivoSelecionado.categoria === 'Social' ? 'bg-red-500' :
                      'bg-orange-500'
                    }`}
                    style={{ width: `${(distintivoSelecionado.progresso / distintivoSelecionado.meta) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Status */}
            {distintivoSelecionado.conquistado ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <i className="ri-check-line text-3xl text-green-600 mb-2"></i>
                <div className="font-bold text-green-900 mb-1">Distintivo Conquistado!</div>
                <div className="text-sm text-green-700">{distintivoSelecionado.dataConquista}</div>
              </div>
            ) : (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                <i className="ri-lock-line text-3xl text-orange-600 mb-2"></i>
                <div className="font-bold text-orange-900 mb-1">Continue Progredindo!</div>
                <div className="text-sm text-orange-700">Complete os requisitos para desbloquear</div>
              </div>
            )}

            {/* Botão Fechar */}
            <button
              onClick={() => setDistintivoSelecionado(null)}
              className="w-full mt-6 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Próximos Distintivos Sugeridos */}
      {proximosDistintivos.length > 0 && (
        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                <i className="ri-lightbulb-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Próximos Distintivos</h3>
                <p className="text-sm text-slate-600">Você está perto de conquistar estes distintivos!</p>
              </div>
            </div>
            <button
              onClick={() => setShowProximosDistintivos(!showProximosDistintivos)}
              className="px-4 py-2 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-100 transition-colors whitespace-nowrap"
              aria-label={showProximosDistintivos ? 'Ocultar sugestões' : 'Ver sugestões'}
            >
              {showProximosDistintivos ? 'Ocultar' : 'Ver Todos'}
            </button>
          </div>

          {showProximosDistintivos && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {proximosDistintivos.map((distintivo) => (
                <div
                  key={distintivo.id}
                  className="bg-white rounded-xl p-4 border border-orange-200 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => setDistintivoSelecionado(distintivo)}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      distintivo.categoria === 'Iniciante' ? 'bg-green-500' :
                      distintivo.categoria === 'Vendas' ? 'bg-blue-500' :
                      distintivo.categoria === 'Serviços' ? 'bg-purple-500' :
                      distintivo.categoria === 'Clientes' ? 'bg-pink-500' :
                      distintivo.categoria === 'Elite' ? 'bg-yellow-500' :
                      distintivo.categoria === 'Jogos' ? 'bg-indigo-500' :
                      distintivo.categoria === 'Social' ? 'bg-red-500' :
                      'bg-orange-500'
                    }`}>
                      <i className={`${getCategoriaIcon(distintivo.categoria)} text-xl text-white`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-slate-900 text-sm truncate">{distintivo.nome}</h4>
                      <p className="text-xs text-slate-600">{distintivo.progresso}/{distintivo.meta}</p>
                    </div>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        distintivo.categoria === 'Iniciante' ? 'bg-green-500' :
                        distintivo.categoria === 'Vendas' ? 'bg-blue-500' :
                        distintivo.categoria === 'Serviços' ? 'bg-purple-500' :
                        distintivo.categoria === 'Clientes' ? 'bg-pink-500' :
                        distintivo.categoria === 'Elite' ? 'bg-yellow-500' :
                        distintivo.categoria === 'Jogos' ? 'bg-indigo-500' :
                        distintivo.categoria === 'Social' ? 'bg-red-500' :
                        'bg-orange-500'
                      }`}
                      style={{ width: `${(distintivo.progresso / distintivo.meta) * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-orange-600 font-semibold mt-2">
                    {Math.round((distintivo.progresso / distintivo.meta) * 100)}% completo
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Botão para testar animação */}
      {distintivos.length > 0 && (
        <div className="flex justify-center">
          <button
            onClick={() => simularConquista(distintivos[0])}
            className="px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-trophy-line text-xl"></i>
            Simular Conquista de Distintivo
          </button>
        </div>
      )}

      {/* Modal de Compartilhamento */}
      {selectedBadge && (
        <ShareModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          achievement={selectedBadge}
        />
      )}

      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(-20px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-confetti {
          animation: confetti linear forwards;
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
